import React, {useCallback, useContext, useRef, useState} from "react"
import Menu, {MenuItem} from "@amzn/meridian/menu";
import Button from "@amzn/meridian/button"
import Icon from "@amzn/meridian/icon";
import menuTokens from "@amzn/meridian-tokens/base/icon/menu-meatball";
import Row from "@amzn/meridian/row";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Text from "@amzn/meridian/text";
import {PickContext} from "./context/PickContext";
import {FormattedMessage} from "react-intl";
import {CapabilityContext} from "../global/context/CapabilityContext";
import {AlertContext} from "../alert/context/AlertContext";
import Column from "@amzn/meridian/column";
import Select, {SelectOption} from "@amzn/meridian/select";
import {PickFilterContext} from "./context/PickFilterContext";
import {ASSIGNABLE, IN_PROGRESS, NOT_ASSIGNED} from "../util/EntityStatusManager";
import CancelConfirmModal from "../commonModals/CancelConfirmModal";
import {AR_V2} from "../util/Constants";

const RouteContextMenu = (props) => {
    const iconRef = useRef()
    const [open, setOpen] = useState(false)
    const onClickButton = useCallback(() => setOpen(true), [])
    const onClose = useCallback(() => setOpen(false), [])

    const [openModal, setOpenModal] = useState(false)
    const [openSidelineModal, setOpenSidelineModal] = useState(false)
    const [openCutRouteModal, setOpenCutRouteModal] = useState(false)
    const onClickFooterButton = useCallback(() => setOpenModal(false), [])
    const onCloseModal = useCallback(() => setOpenModal(false), [])
    const [prioritySlot, setPrioritySlot] = useState()
    const [searchQuery, setSearchQuery] = useState()


    const {dispatch} = useContext(PickContext)
    const {configViewModel} = useContext(CapabilityContext)
    const {alertDispatch} = useContext(AlertContext)
    const {viewModelF, dispatchF} = useContext(PickFilterContext)
    const prioritize = {title: "contextMenu.prioritize", message:"Prioritize"};
    const sideline = {title: "contextMenu.sideline", message:"Sideline"};
    const unAssign = {title: "contextMenu.unAssign", message:"UnAssign"};
    const cutRoute = {title: "contextMenu.cutRoute", message:"Cut route"};

    const onClickConfirm = () => {
        dispatch({
            type: 'PRIORITIZE_ROUTE', data: {
                dispatcher: dispatch,
                routeId: props.route.routeId,
                position: String(prioritySlot),
                stationCode: props.route.stationCode,
                baseUrl: configViewModel.baseUrl,
                alertDispatch: alertDispatch,
                filterDispatcher: dispatchF,
                xptNodes: viewModelF.xptNodes,
                capability: configViewModel.capability,
                stationConfig: configViewModel.stationConfig
            }
        });
        setOpenModal(false)
        setSearchQuery(null)
        setPrioritySlot(null)
    }

    const isRoutePrioritizable = (route, stationConfig) => {
        if (stationConfig && stationConfig.parallelPickEnabled && route.picklists) {
            return route.picklists.some(picklist => (picklist.status === ASSIGNABLE || picklist.status === NOT_ASSIGNED));
        } else {
            return route.status === NOT_ASSIGNED;
        }
    }

    const getMenuItem = () => {
        const menuItem = [];
        if (configViewModel.stationConfig.parallelPickEnabled) {
            if (isRoutePrioritizable(props.route, configViewModel.stationConfig)) {
                menuItem.push(prioritize, sideline)
            } else {
                menuItem.push(sideline)
            }
        } else if (configViewModel.capability === AR_V2) {
            menuItem.push(cutRoute)
        } else {
            if (props.route.status === NOT_ASSIGNED) {
                menuItem.push(prioritize, sideline)
            } else if (props.route.status === IN_PROGRESS) {
                menuItem.push(sideline, unAssign)
            } else {
                menuItem.push(sideline)
            }
        }
        return menuItem;
    };

    const getPrioritySlots = (stationConfig) => {
        const numAssignableRoutes = props.routeList
            .filter(route => isRoutePrioritizable(route, stationConfig) && route.stationCode === props.route.stationCode).length;
        return Array.from({length: numAssignableRoutes}, (_, i) => i + 1)
            .filter(slot => slot !== props.route.priority);
    }

    const matchedPrioritySlots = getPrioritySlots(configViewModel.stationConfig).filter(option => !searchQuery || option.toString().startsWith(searchQuery))

    const executeAction = (menu) => {
        switch (menu) {
            case "Prioritize":
                setOpenModal(true)
                return;
            case "Sideline":
                setOpenSidelineModal(true)
                return;
            case "UnAssign":
                dispatch({
                    type: 'UNASSIGN_ASSOCIATE', data: {
                        dispatcher: dispatch,
                        picklistId: props.route.routeId,    // For normal AMZL site routeId = picklistId
                        associateId: props.route.pickerAlias,
                        stationCode: props.route.stationCode,
                        baseUrl: configViewModel.baseUrl,
                        alertDispatch: alertDispatch,
                        filterDispatcher: dispatchF,
                        xptNodes: viewModelF.xptNodes,
                        capability: configViewModel.capability,
                        stationConfig: configViewModel.stationConfig
                    }
                });
                return;
            case cutRoute.message:
                setOpenCutRouteModal(true)
                return;
            default:
                return;
        }
    }

    const getSidelineConfirmationMessage = () => {
        return <Row alignmentVertical="top" alignmentHorizontal={"center"} spacingInset="none">
            <Column spacingInset={"small"} spacing={"small"}>
                <Text type="h200">
                    <FormattedMessage
                        id="associate.sidelineConfirmationLine1"
                        defaultMessage="Are you sure you want to sideline route "/>
                    {props.route.routeCode}
                </Text>
                <Text type="h200">
                    <FormattedMessage
                        id="associate.sidelineConfirmationLine2"
                        defaultMessage="Note: You will not be able to undo this action."/>
                </Text>
            </Column>
        </Row>
    }

    const onSidelineConfirmation = () => {
        dispatch({
            type: 'SIDELINE_ROUTE', data: {
                dispatcher: dispatch,
                routeId: props.route.routeId,
                stationCode: props.route.stationCode,
                baseUrl: configViewModel.baseUrl,
                alertDispatch: alertDispatch,
                stationConfig: configViewModel.stationConfig
            }
        });
        setOpenSidelineModal(false)
    }

    const onSidelineCancel = () => {
        setOpenSidelineModal(false);
    }

    const getCutRouteConfirmationMessage = () => {
        return <Row alignmentVertical="top" alignmentHorizontal={"center"} spacingInset="none">
            <Column spacingInset={"small"} spacing={"small"}>
                <Text type="h200">
                    <FormattedMessage
                        id="associate.cutRouteConfirmationLine1"
                        defaultMessage="Are you sure you want to cut route "/>
                    {props.route.routeCode}
                </Text>
                <Text type="h200">
                    <FormattedMessage
                        id="associate.sidelineConfirmationLine2"
                        defaultMessage="Note: You will not be able to undo this action."/>
                </Text>
            </Column>
        </Row>
    }

    const onCutRouteConfirmation = () => {
        dispatch({
            type: 'CUT_ROUTE',
            data: {
                dispatcher: dispatch,
                routeId: props.route.routeId,
                stationCode: props.route.stationCode,
                baseUrl: configViewModel.baseUrl,
                alertDispatch: alertDispatch,
                stationConfig: configViewModel.stationConfig
            }
        });
        setOpenCutRouteModal(false)
    }

    const onCutRouteCancel = () => {
        setOpenCutRouteModal(false);
    }

    return (
        <React.Fragment>
            <Button type="icon" ref={iconRef} onClick={onClickButton} size="small">
                <Icon tokens={menuTokens}>Settings</Icon>
            </Button>
            <Menu anchorNode={iconRef.current}
                open={open}
                position="right"
                onClose={onClose}>
                {getMenuItem().map((menu, key) => (
                    <MenuItem key={key} onClick={() => executeAction(menu.message)}>
                        <FormattedMessage id={menu.title} defaultMessage={menu.message}/>
                    </MenuItem>
                ))}
            </Menu>
            <Modal minWidth={"500px"} title="Edit route priority" open={openModal} onClose={onCloseModal} scrollContainer="viewport"
                   closeLabel="Close">
                <Row minWidth={"350px"}  widths={["grid-12"]} alignmentVertical="top" spacingInset="none">
                    <Column spacingInset={"small"} spacing={"small"}>
                        <Text type="h200">
                            <FormattedMessage id="associate.route" defaultMessage="Route"/>
                        </Text>
                        <Text type="b200">
                        {props.route.routeCode}
                        </Text>
                    </Column>
                </Row>
                <Row  widths={["grid-12"]} alignmentVertical="top" spacingInset="none">
                    <Column spacingInset={"small"} spacing={"small"}>
                        <Text type="h200">
                            <FormattedMessage id="pick.station" defaultMessage="Station"/>
                        </Text>
                        <Text type="b200">
                            {props.route.stationCode}
                        </Text>
                    </Column>
                </Row>
                <Row  widths={["grid-12"]} alignmentVertical="top" spacingInset="none">
                    <Column spacingInset={"small"} spacing={"small"}>
                        <Text type="h200">
                            <FormattedMessage id="pick.routeAisles" defaultMessage="Route aisles"/>
                        </Text>
                        <Text type="b200">
                            {props.route.aisles.length ? props.route.aisles.join(", ") : "-"}
                        </Text>
                    </Column>
                </Row>
                <Row  widths={["grid-12"]} alignmentVertical="top" spacingInset="none">
                    <Column spacingInset={"small"} spacing={"small"}>
                        <Text type="h200">
                            <FormattedMessage id="pickStatus.priority" defaultMessage="Priority"/>
                        </Text>
                        <Text type="b200">
                            <FormattedMessage id="pick.changePrioritySummary" defaultMessage="Select the priority slot for this route"/>
                        </Text>
                        <Select width={250}
                                value={prioritySlot}
                                onChange={setPrioritySlot}
                                searchQuery={searchQuery}
                                onSearch={setSearchQuery}
                                placeholder="Priority...">
                            {matchedPrioritySlots.map(slot => (
                                <SelectOption key={slot} label={slot} value={slot}/>
                            ))}
                            {!matchedPrioritySlots.length ? (
                                <Column
                                    width={250}
                                    alignmentVertical="center"
                                    spacing="small"
                                    spacingInset="medium">
                                    <Text alignment="center">No Slots</Text>
                                </Column>
                            ) : null}
                        </Select>
                    </Column>
                </Row>
                <ModalFooter>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="small" onClick={onClickFooterButton}>
                            <FormattedMessage id="route_prioritization_model.cancel" defaultMessage="Cancel"/>
                        </Button>
                        <Button disabled={!prioritySlot} type="primary" size="small" onClick={() => onClickConfirm()}>
                            <FormattedMessage id="route_prioritization_model.confirm" defaultMessage="Confirm"/>
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
            <CancelConfirmModal data ={{
                innerHTML: getSidelineConfirmationMessage(),
                cancelHandler: onSidelineCancel,
                confirmHandler: onSidelineConfirmation,
                visible: openSidelineModal
            }}/>
            <CancelConfirmModal data ={{
                innerHTML: getCutRouteConfirmationMessage(),
                cancelHandler: onCutRouteCancel,
                confirmHandler: onCutRouteConfirmation,
                visible: openCutRouteModal
            }}/>
        </React.Fragment>
    );
}

export default RouteContextMenu


// WEBPACK FOOTER //
// ./src/components/pickStatus/RouteContextMenu.js